import { Block, BlogPageData, BuilderPageContent, SymbolContent } from 'components/builder/types';
import { BlogDataType } from 'components/builder/types/blogs/blogHero';
import {
    BUILDER_BLOG_COMPONENTS_NAMES,
    BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES,
} from '../../components/builder/BuilderComponentsName';
import { getLocationContext } from './getLocationContext';
import { CORPORATE_SITE } from '../constants';

import type { FAQItem } from '@milanlaser/mlcomponents';
import type { State, Metro, Store, MapStoreData } from '../../clinics/clinics';

// Define a type for the object with the 'categories' array inside 'data', Add more properties as needed
interface DataObjectWithCategories {
    data: {
        categories: string[];
        publishedDate: string;
    };
}

// Define the builder state type for location-based pages
export interface LocationBuilderState {
    state: {
        model: 'state-page' | 'metro-page' | 'clinic-page';
        state?: State;
        metro?: Metro;
        store?: Store;
        mapData?: MapStoreData[];
    };
}

type SortOption = 'newest' | 'oldest';

//Write a function to Calculate the midpoint for splitting the array
export function splitArray<T>(arr: T[]) {
    const midPoint = Math.ceil(arr.length / 2);
    const firstColumnFAQs = arr?.slice(0, midPoint);
    const secondColumnFAQs = arr?.slice(midPoint);
    return { firstColumnFAQs, secondColumnFAQs, midPoint };
}

// Function to create a unique array of categories
export const uniqueCategories = (categories: string[]) => {
    return Array.from(new Set(categories));
};

// Define the function to filter based on categories
export function filterByCategories<T extends DataObjectWithCategories>(objects: T[], categories: string[]): T[] {
    return objects.filter((obj) => obj.data && obj.data.categories.some((category) => categories.includes(category)));
}

export function sortBlogs<T extends DataObjectWithCategories>(data: T[], sortOption: SortOption): T[] {
    if (!data) return [];
    return data.sort((a, b) => {
        const aDate = new Date(a.data.publishedDate).getTime();
        const bDate = new Date(b.data.publishedDate).getTime();
        if (sortOption === 'newest') {
            return bDate - aDate; // Newest first (larger timestamp comes first)
        } else {
            return aDate - bDate; // Oldest first (smaller timestamp comes first)
        }
    });
}

export const capitalizeFirstLetter = (str: string): string => {
    return str
        .split(' ') // Split the string by spaces into an array of words
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize the first letter and lowercase the rest
        .join(' '); // Join the words back together into a string
};

/**
 * Adds 'All Categories' to the categories array if it doesn't exist
 * @param blogs - Array of blog data objects
 * @returns Updated array of blog data with 'All Categories' added to categories
 */
export const addAllCategoriesToBlogs = (blogs: BlogDataType[]): BlogDataType[] => {
    return blogs.map((blog) => {
        const currentCategories = blog.data.categories || [];

        // Only add 'All Categories' if it's not already present
        if (!currentCategories.includes('All Categories')) {
            return {
                ...blog,
                data: {
                    ...blog.data,
                    categories: ['All Categories', ...currentCategories],
                },
            };
        }

        return blog;
    });
};

/**
 * Removes 'All Categories' from the categories array if it exists
 * @param blogs - Array of blog data objects
 * @returns Updated array of blog data with 'All Categories' removed from categories
 */
export const removeAllCategoriesFromBlogs = (blogs: BlogDataType[]): BlogDataType[] => {
    return blogs.map((blog) => {
        const currentCategories = blog.data.categories || [];

        // Remove 'All Categories' if it exists
        const filteredCategories = currentCategories.filter((category) => category !== 'All Categories');

        // Only create a new object if we actually removed 'All Categories'
        if (filteredCategories.length !== currentCategories.length) {
            return {
                ...blog,
                data: {
                    ...blog.data,
                    categories: filteredCategories,
                },
            };
        }

        return blog;
    });
};

/**
 * Removes 'All Categories' from a string array if it exists
 * @param categories - Array of category strings
 * @returns New array with 'All Categories' removed if it existed
 */
export const removeAllCategoriesFromStringArr = (categories: string[]): string[] => {
    return categories.filter((category) => category !== 'All Categories');
};

//   /**
//    * Gets the blogs array with results from BuilderComponentState
//    * @param builderState - The BuilderComponentState object
//    * @returns The blogs array with results, or undefined if not found
//    */
//     export  const getBlogsWithResults = (builderState: BuilderComponentState): BlogDataType[] | undefined => {
//       if (!builderState?.state) return undefined;

//       // Find the first key that starts with 'blogs' and has a results array
//       const blogsKey = Object.keys(builderState.state).find(
//           (key) => key.startsWith('blogs') && Array.isArray(builderState.state[key]?.results),
//       );

//       return blogsKey ? builderState.state[blogsKey].results : undefined;
//   };

/**
 * Compares URLs between BlogPageData and BlogDataType arrays
 * @param blogPages Array of BlogPageData objects
 * @param blogData Array of BlogDataType objects
 * @returns Object containing matching and non-matching URLs
 */
export const compareUrls = (
    blogPages: BlogPageData[],
    blogData: BlogDataType[]
): {
    matchingUrls: string[];
    nonMatchingBlogPageUrls: string[];
    matchingBlogData: BlogDataType[];
} => {
    // Extract URLs from blogPages
    const blogPageUrls = blogPages?.map((page) => page?.data?.url);

    // Initialize result object
    const result = {
        matchingUrls: [] as string[],
        nonMatchingBlogPageUrls: [] as string[],
        matchingBlogData: [] as BlogDataType[],
    };

    // Check each URL from blogPages
    blogPageUrls?.forEach((url) => {
        // Find matching blog data
        const matchingBlog = blogData?.find((blog) => {
            const derivedUrl = blog?.data?.pageUrl || generateUrlFromHeadline(blog?.data?.headline || '');
            return url === derivedUrl;
        });

        if (matchingBlog) {
            result.matchingUrls.push(url);
            result.matchingBlogData.push(matchingBlog);
        } else {
            result.nonMatchingBlogPageUrls.push(url);
        }
    });

    return result;
};

/**
 * Helper function to generate a URL from a headline
 */
const generateUrlFromHeadline = (headline: string): string => {
    const headlineUrl = headline
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    return '/' + headlineUrl; // adding '/' to the beginning of the URL before returning
};

export const getSymbolsBlockContentFromBuilderData = (data: BuilderPageContent, symbol: SymbolContent[]) => {
    const resolvedBlocks = data?.blocks?.map((block) => {
        // Check if the block is a Symbol component and has an entry ID
        if (block?.component?.name === 'Symbol' && block?.component?.options?.symbol?.entry) {
            const symbolId = block.component.options.symbol.entry;
            const symbolData = symbol.find((symbol) => symbol.id === symbolId); // Find the symbol by its ID
            return symbolData?.content?.data?.blocks?.[0] || block; // Return the first block of the symbol, or the original block if not found
        }
        return block;
    });
    return resolvedBlocks;
};

/**
 * Helper function to strip HTML tags from text
 * @param html - The HTML string to strip tags from
 * @returns The text with HTML tags removed
 */
export const stripHtmlTags = (html: string): string => {
    return html.replace(/<\/?[^>]+(>|$)/g, '');
};

/**
 * Checks if rich text HTML content has actual text content
 * @param htmlString - The HTML string to check
 * @returns boolean indicating if there is actual text content (not just empty tags)
 */
export const hasRichTextContent = (htmlString: string | null | undefined): boolean => {
    if (!htmlString) return false;
    const textContent = htmlString.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 0;
};

/**
 * Checks if a block is a FAQ component
 * @param block - The content block to check
 * @returns boolean indicating if the block is a FAQ component
 */
const isFAQComponent = (block: Block): boolean => {
    return (
        block?.component?.name === BUILDER_BLOG_COMPONENTS_NAMES.BlogFAQs ||
        block?.component?.name === BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES.S6FAQs
    );
};

/**
 * Generates dynamic location-based FAQs for state/metro/clinic pages
 * @param locationName - The name of the location (state, metro, or clinic)
 * @returns An array of dynamic FAQ data
 */
export const getDynamicLocationFAQs = (locationName: string) => {
    if (!locationName) return [];

    return [
        {
            question: `What's The Best Laser Hair Removal Clinic In ${locationName}?`,
            answer: `Milan Laser is the best choice for safe, effective laser hair removal treatments in ${locationName}. All skin tones are treated with advanced laser technology from medical professionals and results from every laser treatment are permanent.`,
        },
        {
            question: `How Much Does ${locationName} Laser Hair Removal Cost?`,
            answer: `The cost of laser hair removal in ${locationName} may vary depending on the body areas treated, financing offered, and any laser hair removal specials. If you go somewhere that charges by the session, you may pay more than somewhere that offers unlimited laser treatments for one price.`,
        },
    ];
};

/**
 * Extracts FAQ data from content blocks with optional dynamic location FAQs
 * @param blocks - The content blocks to extract FAQ data from
 * @param builderState - Optional builder state for dynamic location FAQs
 * @returns An array of FAQ data
 */
export const extractFAQsFromContent = (blocks: Block[], builderState?: LocationBuilderState) => {
    if (!blocks || !Array.isArray(blocks)) return [];

    const faqBlocks = blocks.filter(
        (block) => isFAQComponent(block) && (block?.component?.options as { faqs?: FAQItem[] })?.faqs
    );

    const staticFaqs = faqBlocks.flatMap((block) => {
        const options = block?.component?.options as { faqs?: FAQItem[] };
        const faqs = options?.faqs;

        if (!Array.isArray(faqs)) {
            return [];
        }

        return faqs.filter(
            (faq): faq is FAQItem =>
                faq && typeof faq === 'object' && typeof faq.question === 'string' && typeof faq.answer === 'string'
        );
    });

    if (builderState) {
        const model = builderState.state?.model;
        const isLocationPage = ['state-page', 'metro-page', 'clinic-page'].includes(model);

        if (isLocationPage) {
            const locationName = getLocationContext(builderState);
            const dynamicLocationFaqs = locationName ? getDynamicLocationFAQs(locationName) : [];

            const clinicFaqs = model === 'clinic-page' ? builderState.state?.store?.clinic_faqs : undefined;

            if (clinicFaqs?.length) {
                return [...dynamicLocationFaqs, ...staticFaqs, ...clinicFaqs];
            }
            const metroFaqs = model === 'metro-page' ? builderState.state?.metro?.metro_faq : undefined;

            if (metroFaqs?.length) {
                return [...dynamicLocationFaqs, ...staticFaqs, ...metroFaqs];
            }

            if (locationName) {
                return [...dynamicLocationFaqs, ...staticFaqs];
            }
        }
    }

    return staticFaqs;
};

/**
 * Checks if a block is a breadcrumb component
 * @param block - The content block to check
 * @returns boolean indicating if the block is a breadcrumb component
 */
const isBreadcrumbComponent = (block: Block): boolean => {
    return block?.component?.name === BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES.S40Breadcrumbs;
};

/**
 * Extracts breadcrumb segments from content blocks for structured data
 * @param blocks - The content blocks to extract breadcrumb data from
 * @param builderState - Builder state containing location information
 * @returns An array of breadcrumb segments or null if no breadcrumbs found
 */
export const extractBreadcrumbsFromContent = (blocks: Block[], builderState: LocationBuilderState) => {
    if (!blocks?.length || !builderState || !blocks.some(isBreadcrumbComponent)) {
        return null;
    }

    const { state, metro, store, model } = builderState.state;
    if (!state) return null;

    const stateAbbreviation = state.abbreviation.toLowerCase();
    const segments = [
        { label: 'Home', href: `${CORPORATE_SITE}` },
        { label: 'Locations', href: `${CORPORATE_SITE}/locations` },
        { label: state.name, href: `${CORPORATE_SITE}/locations/${stateAbbreviation}` },
    ];

    if (model === 'state-page') {
        return segments;
    }

    if (!metro) return null;

    const storeWithUrl = metro.stores?.find((s) => s.urlPath);
    const metroUrl = storeWithUrl?.urlPath?.split('/').slice(0, -1).join('/');
    if (!metroUrl) return null;

    const metroBreadcrumb = { label: metro.name, href: `${CORPORATE_SITE}${metroUrl}` };

    switch (model) {
        case 'metro-page':
            return [...segments, metroBreadcrumb];

        case 'clinic-page':
            return store
                ? [
                      ...segments,
                      metroBreadcrumb,
                      { label: store.marketingCity, href: `${CORPORATE_SITE}${store.urlPath}` },
                  ]
                : null;

        default:
            return segments;
    }
};

/**
 * Determines if a page should include service schema based on URL patterns
 * @param url - The page URL to check
 * @returns boolean indicating if service schema should be included
 */
export const shouldIncludeServiceSchema = (url: string): boolean => {
    if (!url) return false;

    // Normalize URL (remove trailing slash for consistency)
    const normalizedUrl = url.endsWith('/') ? url.slice(0, -1) : url;

    // Specific individual pages
    const specificPages = [
        '/what-is-laser-hair-removal',
        '/how-to-prepare-for-laser-hair-removal',
        '/laser-hair-removal-aftercare',
    ];

    if (specificPages.includes(normalizedUrl)) {
        return true;
    }

    // URL pattern matching
    return normalizedUrl.startsWith('/areas') || normalizedUrl.startsWith('/inclusive-clientele');
};
